import { MaybeNil } from './interfaces';
import { isNil } from 'lodash';

// must be in ascending order: second -> minute -> hour -> day -> week -> year etc...
const formattingUnits = [
  { divisor: 1, unit: 's' },
  { divisor: 60, unit: 'm' },
  { divisor: 60 * 60, unit: 'h' },
  { divisor: 24 * 60 * 60, unit: 'd' },
  { divisor: 7 * 24 * 60 * 60, unit: 'w' },
] as const;

/**
 * Display number of seconds as "1h 2m 12s
 * @param valueInSeconds
 */
export const formatPeriod = (valueInSeconds: MaybeNil<number>) => {
  if (isNil(valueInSeconds)) {
    return null;
  }

  // calculate the value for each unit. Assumes that formattingUnits are in ascending order
  // When iterating backwards, we can reduce the value with the remainder of the previous division
  const perUnitValues: Array<{ value: number; unit: string }> = [];
  let remainderValue = valueInSeconds;
  for (let i = formattingUnits.length - 1; i >= 0; i--) {
    const fu = formattingUnits[i];
    const value = Math.floor(remainderValue / fu.divisor);
    perUnitValues.push({ value, unit: fu.unit });
    remainderValue %= fu.divisor;
  }

  // find the indicies of the first non-zero value from the beginning and from the end
  // we want to omit leading and trailing zeros: 1h 2m vs. 0d 1h 2m 0s
  // but we want to keep zeros in between: 1h 0m 2s, 1d 0h 2m
  let fromIndex: number | null = null;
  let endIndex: number | null = null;
  for (let i = 0; i < perUnitValues.length; i++) {
    const v = perUnitValues[i];
    if (v.value !== 0) {
      if (fromIndex === null) {
        fromIndex = i;
      }
      endIndex = i;
    }
  }

  // trim to non-zero values
  let toPrint: typeof perUnitValues;
  if (fromIndex != null && endIndex !== null) {
    toPrint = perUnitValues.slice(fromIndex, endIndex + 1); // slice() end argument is exclusive
  } else {
    toPrint = [{ value: 0, unit: formattingUnits[0].unit }];
  }

  return toPrint.map((v) => `${v.value}${v.unit}`).join(' ');
};
